import classNames from "classnames";
import { AppNavigation } from "components/AppNavigation/AppNavigation";
import { Button } from "components/DesignSystem/Button/Button";
import HoverCard from "components/DesignSystem/HoverCard/HoverCard";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { PermissionBasedView } from "components/PermissionBasedView/PermissionBasedView";
import { ProfileDropdown } from "components/ProfileDropdown/ProfileDropdown";
import { ConditionalLink } from "components/conditionalLink";
import DashboardContainer from "components/dashboard/DashboardContainer";
import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import { CaretRight } from "components/icons/CaretRight";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { DoubleArrow } from "components/icons/DoubleArrow";
import { InkleAdminBooksLogo } from "components/icons/Logos/InkleAdminBooksLogo";
import { InkleAdminBooksSymbol } from "components/icons/Logos/InkleAdminBooksSymbol";
import { InkleAdminLogo } from "components/icons/Logos/InkleAdminLogo";
import { InklePracticeLogo } from "components/icons/Logos/InklePracticeLogo";
import { InklePracticeProLogo } from "components/icons/Logos/InklePracticeProLogo";
import { InklePracticeProSymbol } from "components/icons/Logos/InklePracticeProSymbol";
import { InklePracticeSymbol } from "components/icons/Logos/InklePracticeSymbol";
import { InkleSalesTaxLogo } from "components/icons/Logos/InkleSalesTaxLogo";
import { InkleSalesTaxSymbol } from "components/icons/Logos/InkleSalesTaxSymbol";
import { InkleTaxSymbol } from "components/icons/Logos/InkleTaxSymbol";
import { DoubleArrowIcon } from "components/icons/DoubleArrowIcon";
import {
  NAVBAR_COLLAPSER_CLICKED,
  PROFILE_DROPDOWN_CLICKED_FROM_SIDEBAR,
  SIDE_NAV_BUTTON_CLICKED,
} from "constants/analyticsEvents";
import { useCurrentAppContext } from "contexts/CurrentAppContext";
import { AnimatePresence, motion } from "framer-motion";
import { useAnalytics } from "hooks/useAnalytics";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useWindowSize } from "hooks/useWindowSize";
import { parse, stringify } from "qs";
import {
  ComponentProps,
  ComponentType,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { Timeout } from "react-number-format/types/types";
import {
  matchPath,
  NavLink,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { useLocalStorage } from "hooks/useLocalStorage";
import { usePageTitleSetter } from "hooks/usePageTitleSetter";
import { useSecondaryNav } from "components/AppLayout/SecondaryNavContext";

export const LeftBarOpenContext = createContext<{
  open: boolean;
  setOpen?: (data: boolean) => void;
}>({ open: true });

export const LeftBarLogo = ({
  logo: LogoFromProps,
  symbol: SymbolFromProps,
  url: _urlFromprops,
}: {
  logo: ComponentType;
  symbol: ComponentType;
  url?: string;
}) => {
  const { open } = useContext(LeftBarOpenContext);
  const { isAdmin, isCpa, isForeignCA, isPersonalTaxUser } = useRoleBasedView();
  const { currentApp } = useCurrentAppContext();

  let Logo = LogoFromProps;
  let Symbol = SymbolFromProps;

  if (isForeignCA && !isAdmin && !isCpa) {
    Logo = InklePracticeLogo;
    Symbol = InklePracticeSymbol;
  }

  if (isCpa && !isAdmin && !isForeignCA) {
    Logo = InklePracticeProLogo;
    Symbol = InklePracticeProSymbol;
  }

  if (isAdmin) {
    Logo = InkleAdminLogo;
    Symbol = InkleTaxSymbol;
  }
  if (isAdmin && currentApp.name === "Sales Tax Admin") {
    Logo = InkleSalesTaxLogo;
    Symbol = InkleSalesTaxSymbol;
  }

  if (isAdmin && currentApp.name === "Books Admin") {
    Logo = InkleAdminBooksLogo;
    Symbol = InkleAdminBooksSymbol;
  }

  if (isPersonalTaxUser) {
    return (
      <div
        className={classNames("t-z-10 t-flex t-h-9 t-items-center", {
          "t-px-3": open,
          "t-justify-center": !open,
        })}
      >
        {open ? <Logo /> : <Symbol />}
      </div>
    );
  }

  return (
    <div className="t-z-10">
      <AppNavigation>
        <div
          className={classNames(
            "t-flex t-h-9 t-cursor-pointer t-items-center t-rounded !t-border-none focus-visible:t-outline-none hover:t-bg-surface-grey data-[highlighted]:t-bg-surface-lighter-grey",
            {
              "t-gap-1.5 t-px-3": open,
              "t-justify-center": !open,
            }
          )}
        >
          {open ? (
            <>
              <Logo />
              <DoubleArrow />
            </>
          ) : (
            <Symbol />
          )}
        </div>
      </AppNavigation>
    </div>
  );
};

export const LeftBarProfileDropdownItem = ({
  icon: Icon,
  children,
  suffix,
  to,
  useParentPath = true,
  type = "regular",
  onClick,
  ...props
}: {
  icon: ComponentType;
  children: ReactNode;
  suffix?: ReactNode;
  to?: string;
  useParentPath?: boolean;
  type?: "danger" | "regular";
  onClick?: () => void;
} & ComponentProps<typeof ConditionalLink>) => {
  const { path } = useRouteMatch();
  const { pathname, search } = useLocation();

  const redirectPath = useParentPath ? `${path}${to}` : to;
  const redirectPathWithoutSearch =
    redirectPath?.split("?")?.[0] || redirectPath;
  const searchInPath = redirectPath?.split("?")?.[1];

  const parsedSearch = parse(searchInPath || "", {
    ignoreQueryPrefix: true,
  });

  const newSearch = stringify(
    { ...parsedSearch, settings_back_to: `${pathname}${search}` },
    {
      skipNulls: true,
      addQueryPrefix: true,
    }
  );

  const finalRedirect = `${redirectPathWithoutSearch}${newSearch}`;

  if (to) {
    return (
      <ConditionalLink
        {...props}
        to={finalRedirect}
        className={classNames(
          "t-relative t-py-2 t-px-4 t-flex t-gap-2 t-items-center t-border-solid t-border-0 t-transition-all t-no-underline hover:t-bg-neutral-0 t-rounded",
          {
            "t-group/regular": type === "regular",
            "t-group/danger hover:t-bg-red-10": type === "danger",
          }
        )}
      >
        <span className="t-flex group-[&]/regular:t-text-text-30 group-[&]/danger:t-text-red">
          {Icon && <Icon />}
        </span>
        <span className="t-text-body-sm  group-[&]/danger:t-text-red group-[&]/regular:t-text-text-60">
          {children}
        </span>
        {suffix}
      </ConditionalLink>
    );
  }

  return (
    <button
      onClick={onClick}
      className={classNames(
        "t-relative t-px-4 t-py-2 t-flex t-gap-2 t-items-center t-border-solid t-border-0 t-transition-all t-no-underline hover:t-bg-neutral-0 t-rounded all:unset",
        {
          "t-group/regular": type === "regular",
          "t-group/danger hover:t-bg-red-10": type === "danger",
        }
      )}
    >
      <span className="t-flex group-[&]/regular:t-text-text-100 group-[&]/danger:t-text-red t-text-text-30">
        <Icon />
      </span>
      <span className="t-text-body-sm group-[&]/active-link:t-text-text-100 group-[&]/danger:t-text-red group-[&]/active-link:t-font-medium t-text-text-60">
        {children}
      </span>
      {suffix}
    </button>
  );
};

export const LeftBarProfileDropdown = ({
  name,
  org,
  email,
  imageUrl,
  role,

  children,
}: {
  name: string;
  org?: string | null;
  imageUrl: string;
  email: string;
  role?: string | null;
  children: ReactNode;
}) => {
  const { open } = useContext(LeftBarOpenContext);
  const { trackEvent } = useAnalytics();
  const [pop, setPop] = useState(false);
  const closeRef = useRef<Timeout>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { isMobile } = useWindowSize();

  const onClose = () => {
    if (closeRef.current) {
      clearTimeout(closeRef.current);
    }

    // @ts-ignore
    closeRef.current = setTimeout(() => {
      setPop(false);
    }, 200);
  };

  const onOpen = () => {
    closeRef.current && clearTimeout(closeRef.current);
    setPop(true);
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackEvent(PROFILE_DROPDOWN_CLICKED_FROM_SIDEBAR);

    if (isMobile) {
      setPop((prev) => !prev);
    }
  };

  // Tapping the trigger is an "outside" tap for the card; let the click toggle it instead
  const keepOpenOnTriggerTap = (e: Event) => {
    const { target } = e;

    if (target instanceof Node && triggerRef.current?.contains(target)) {
      e.preventDefault();
    }
  };

  return (
    <HoverCard.Root
      open={pop}
      openDelay={300}
      onOpenChange={isMobile ? setPop : undefined}
    >
      <HoverCard.Trigger
        asChild
        onClick={handleTriggerClick}
        onMouseEnter={isMobile ? undefined : onOpen}
        onMouseLeave={isMobile ? undefined : onClose}
      >
        <button
          ref={triggerRef}
          className={classNames(
            "t-flex t-items-center t-gap-2 all:unset t-box-border t-py-3 t-w-full t-bg-transparent",
            {
              "t-pl-4 t-pr-3": open,
              "t-justify-center t-px-0": !open,
            }
          )}
        >
          <img
            src={imageUrl}
            alt="profile"
            className="t-w-7 t-h-7 t-shrink-0 t-rounded-full t-object-cover"
          />
          {open && (
            <>
              <div className="t-flex t-min-w-0 t-flex-1 t-flex-col t-justify-center">
                <span className="t-text-text-100 t-font-medium t-truncate t-block t-text-body-sm">
                  {name}
                </span>
                {org && (
                  <span className="t-text-caption t-text-text-30 t-truncate t-block">
                    {org}
                  </span>
                )}
              </div>
              <span className="t-flex t-shrink-0 t-text-text-30">
                <CaretRight className="t-size-3.5" />
              </span>
            </>
          )}
        </button>
      </HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content
          className="t-min-w-[256px]"
          align="end"
          onMouseEnter={onOpen}
          onMouseLeave={onClose}
          onPointerDownOutside={keepOpenOnTriggerTap}
          side={isMobile ? "top" : "right"}
          sideOffset={10}
          alignOffset={isMobile ? 0 : 16}
        >
          <div className="t-w-full t-p-2 t-font-medium">
            <div className="t-flex t-gap-3 all:unset t-w-full t-py-2 t-px-3">
              <img
                src={imageUrl}
                alt="profile"
                className="t-w-8 t-h-8 t-rounded-full"
              />
              <div className="t-flex t-min-w-0 t-flex-1 t-items-start t-gap-2">
                <div className="t-flex t-flex-col t-min-w-0 t-flex-1 t-overflow-hidden">
                  <span className="t-text-text-100 t-font-medium t-truncate t-block t-text-subtitle-sm">
                    {name}
                  </span>
                  <span className="t-text-body-sm t-text-text-30 t-truncate t-block">
                    {email}
                  </span>
                </div>
                {role && (
                  <Tag tagType="orange" icon={false} rounded size="small">
                    {role}
                  </Tag>
                )}
              </div>
            </div>

            <div className="t-py-2">
              <LeftBarSeparator />
            </div>

            <div className="t-flex t-gap-2 t-flex-col">{children}</div>
          </div>
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
};

export const LeftBarSeparator = ({ name }: { name?: string }) => {
  const { open } = useContext(LeftBarOpenContext);

  return (
    <div className="t-flex t-items-center">
      {name && open && (
        <span className="t-text-caption t-whitespace-nowrap t-px-3 t-text-text-30 t-uppercase t-tracking-wider">
          {name}
        </span>
      )}
      <span className="t-h-px t-bg-neutral-10 t-w-full" />
    </div>
  );
};

export const Anchor = (props: ComponentProps<typeof NavLink>) => (
  <a
    {...props}
    className={
      typeof props.className === "function"
        ? props.className?.(false)
        : props.className
    }
    href={props.to.toString()}
    style={
      typeof props.style === "function" ? props.style?.(false) : props.style
    }
  />
);

const LeftBarItemBase = ({
  icon: Icon,
  children,
  suffix,
  to,
  useParentPath = true,
  disabled,
  ...props
}: {
  icon: ComponentType;
  children: ReactNode;
  suffix?: ReactNode;
  to: string;
  useParentPath?: boolean;
  disabled?: boolean;
} & ComponentProps<typeof NavLink>) => {
  const { path } = useRouteMatch();
  const { open } = useContext(LeftBarOpenContext);
  const location = useLocation();
  const setPageTitle = usePageTitleSetter();
  const id = useId();

  const isExternal =
    to?.startsWith("https://") ||
    to?.startsWith("http://") ||
    to?.startsWith("mailto") ||
    to?.startsWith("tel");

  let LinkComponent = isExternal ? Anchor : NavLink;

  const { currentApp } = useCurrentAppContext();
  const { trackEvent } = useAnalytics();

  const resolvedPath = (useParentPath ? `${path}${to}` : to)?.split("?")[0];
  const normalizedPath = resolvedPath?.replace(/\/+$/, "") || "/";
  const isActiveRoute =
    !isExternal &&
    Boolean(
      matchPath(location.pathname, {
        path: normalizedPath,
        exact: Boolean(props.exact),
      })
    );

  useEffect(() => {
    if (isActiveRoute && typeof children === "string") {
      setPageTitle(children);
    }
  }, [isActiveRoute, children, setPageTitle]);

  const handleLink = () => {
    trackEvent(SIDE_NAV_BUTTON_CLICKED, {
      app_name: currentApp.name,
      name: children as string,
    });
  };

  return (
    <ConditionalToolTip condition={!open && children} side="right">
      <div className="t-w-full t-cursor-pointer" onClick={handleLink}>
        <LinkComponent
          {...props}
          to={useParentPath ? `${path}${to}` : to}
          target={to.includes("http") ? "_blank" : undefined}
          // @ts-ignore
          className={(isActive) =>
            classNames(
              "t-relative t-h-9 t-my-0.5 t-flex t-w-full t-gap-2 t-items-center t-overflow-hidden t-border-solid t-border-0 t-transition-all t-rounded-md t-no-underline focus-visible:t-outline-purple-30",
              {
                "t-px-3": open,
                "t-justify-center t-px-0": !open,
                "hover:t-bg-neutral-0 *:t-group/non-active-link": !isActive,
                "t-bg-neutral-0 t-group/active-link": isActive,
                "t-text-text-30 t-pointer-events-none": disabled,
              }
            )
          }
        >
          <span className="t-absolute t-left-0 t-top-1/2 -t-translate-y-1/2 t-h-4 t-w-0.5 t-rounded-r t-bg-purple-70 t-hidden group-[&]/active-link:t-block" />
          <span
            className={classNames(
              "t-flex t-h-5 t-w-5 t-shrink-0 t-items-center t-justify-center group-[&]/active-link:t-text-text-100",
              {
                "t-text-text-30": !disabled,
                "t-text-neutral-30": disabled,
              }
            )}
          >
            <Icon />
          </span>

          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                key={`content-open-${id}`}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.1 }}
                className="t-flex t-gap-2 t-items-center t-justify-between t-w-full t-min-w-0"
              >
                <span
                  className={classNames(
                    "t-text-body-sm group-[&]/active-link:t-text-text-100 group-[&]/active-link:t-font-medium t-whitespace-nowrap t-overflow-hidden",
                    {
                      "t-text-text-30": disabled,
                      "!t-text-text-60 group-hover/non-active-link:t-text-text-100":
                        !disabled,
                    }
                  )}
                >
                  {children}
                </span>

                {suffix}
              </motion.div>
            )}
          </AnimatePresence>
        </LinkComponent>
      </div>
    </ConditionalToolTip>
  );
};

export const LeftBarItem = (props: ComponentProps<typeof LeftBarItemBase>) => {
  const { path } = useRouteMatch();
  const currentPath =
    props.useParentPath === false ? props.to : `${path}${props.to}`;

  return (
    <PermissionBasedView
      route={currentPath.split("?")[0]}
      isNav
      blockedUI={
        <LeftBarItemBase
          suffix={<DiamondPurple height={12} width={12} />}
          {...props}
        />
      }
    >
      {<LeftBarItemBase {...props} />}
    </PermissionBasedView>
  );
};

export const LeftBarTag = ({
  type,
  children,
  className,
}: {
  type: "red" | "blue" | "closed" | "purple" | "purple-closed" | "neutral";
  children: ReactNode;
  className?: string;
}) => {
  const isCountChip = type === "red" || type === "neutral";

  return (
    <div
      className={classNames(
        "t-text-caption t-rounded-full t-leading-normal t-font-medium t-tabular-nums",
        {
          "t-flex t-h-4 t-min-w-4 t-items-center t-justify-center t-px-1":
            isCountChip,
          "t-px-1.5 t-py-0.5": !isCountChip,
          "t-bg-red t-text-white": type === "red",
          "t-bg-neutral-10 t-text-neutral-70": type === "neutral",
          "t-bg-blue-10 t-text-blue-80 t-border-blue-20 t-border t-border-solid":
            type === "blue",
          "t-bg-blue-10 t-text-blue-80 t-border-blue-20 t-border t-border-solid t-absolute -t-top-1 -t-right-1":
            type === "closed",
          "t-bg-purple-10 t-text-purple-80 t-border-purple-20 t-border t-border-solid":
            type === "purple",
          "t-bg-purple-10 t-text-purple-80 t-border-purple-20 t-border t-border-solid t-absolute -t-top-1 -t-right-1":
            type === "purple-closed",
        },
        className
      )}
    >
      {children}
    </div>
  );
};

export const LeftBar = ({
  children,
  logo,
  subHeader,
  stickyNav,
  profile,
  useInternalLink,
  showCollapse,
}: {
  children: ReactNode;
  logo?: ReactNode;
  subHeader?: ReactNode;
  stickyNav?: ReactNode;
  profile?: ReactNode;
  useInternalLink?: boolean;
  showCollapse?: boolean;
}) => {
  const { localData: open, setItem: setOpen } = useLocalStorage<boolean>(
    "leftBarOpen",
    true
  );
  const { trackEvent } = useAnalytics();
  const { active: secondaryActive } = useSecondaryNav();
  const [secExpanded, setSecExpanded] = useState(false);

  const effectiveOpen = secondaryActive ? secExpanded : open;

  useEffect(() => {
    if (!secondaryActive) {
      setSecExpanded(false);
    }
  }, [secondaryActive]);

  const handleNavbarCollapse = () => {
    if (secondaryActive) {
      setSecExpanded((value) => !value);
    } else {
      setOpen(!open);
    }
    trackEvent(NAVBAR_COLLAPSER_CLICKED);
  };

  return (
    <LeftBarOpenContext.Provider value={{ open: effectiveOpen, setOpen }}>
      <motion.div
        transition={{ duration: 0.3 }}
        className={classNames(
          "t-bg-transparent t-h-full t-flex t-flex-col t-transition-all t-duration-30 t-relative",
          {
            "md:t-w-[184px]": effectiveOpen,
            "md:t-w-[56px]": !effectiveOpen,
          }
        )}
      >
        {showCollapse && (
          <div className="t-hidden md:t-block t-absolute t-top-1/2 -t-translate-y-1/2 -t-right-2.5 t-translate-x-1/2 t-z-sidebar">
            <ToolTip text={effectiveOpen ? "Collapse" : "Expand"} side="right">
              <div className="t-flex t-bg-surface t-border t-border-solid t-border-neutral-10 t-rounded-full t-shadow-light-30 t-transition-colors hover:t-border-purple-40">
                <button onClick={handleNavbarCollapse} className="all:unset">
                  <DoubleArrowIcon
                    className={classNames("!t-size-6 t-text-text-30", {
                      "t-rotate-180": !effectiveOpen,
                    })}
                  />
                </button>
              </div>
            </ToolTip>
          </div>
        )}

        <DashboardContainer className="t-h-full">
          {logo && (
            <DashboardContainer.Header className="t-mb-1">
              {logo}
              {subHeader && (
                <div className="t-pt-4 t-flex t-flex-col t-gap-2">
                  {subHeader}
                </div>
              )}
            </DashboardContainer.Header>
          )}
          <DashboardContainer.Content className="t-flex t-min-h-0 t-flex-auto t-flex-col t-overflow-y-auto t-pt-2">
            {children}
          </DashboardContainer.Content>
          <div className="t-shrink-0">
            {stickyNav && (
              <div className="t-flex t-flex-col t-py-1 t-border-solid t-border-0 t-border-t t-border-b t-border-neutral-10">
                {stickyNav}
              </div>
            )}
            {profile || <ProfileDropdown useInternalLink={useInternalLink} />}
          </div>
        </DashboardContainer>
      </motion.div>
    </LeftBarOpenContext.Provider>
  );
};

export const useLeftBar = () => {
  const context = useContext(LeftBarOpenContext);
  if (!context)
    throw new Error("useLeftBar must be used within LeftbarProvider");
  return context;
};
